import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Phone, Mail, DollarSign, Calendar, Star, Clock, Check, XCircle, AlertTriangle, Plus, Send, X, FileText, MessageSquare } from 'lucide-react';
import { formatFullName, formatPhone, formatCurrency, formatDate } from '../utils/helpers';
import { employeeRoles } from '../utils/sampleData';
import { format, parseISO, differenceInDays, isPast, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { EmployeeReviewSection } from '../components/PerformanceReview';
import EmployeeDocuments from '../components/EmployeeDocuments';
import EmployeeConversations from '../components/EmployeeConversations';
import './EmployeeDetail.css';

function EmployeeDetail() {
  const { id } = useParams();
  const { getEmployeeById, getEmployeeTimeOff, addTimeOffRequest, approveTimeOffRequest, isOwnTimeOffRequest, state } = useApp();
  
  const employee = getEmployeeById(id);

  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    type: 'pto',
    reason: '',
    autoApprove: true
  });

  // Hours worked date range (defaults to last 30 days)
  const [hoursRange, setHoursRange] = useState(() => ({
    startDate: format(subDays(new Date(), 29), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  }));
  const [timeRecords, setTimeRecords] = useState([]);
  const [loadingHours, setLoadingHours] = useState(false);

  useEffect(() => {
    if (!id || !hoursRange.startDate || !hoursRange.endDate) return;
    let cancelled = false;
    const loadHours = async () => {
      setLoadingHours(true);
      try {
        const recordsRef = collection(db, 'timeRecords');
        const q = query(
          recordsRef,
          where('employeeId', '==', id),
          where('date', '>=', hoursRange.startDate),
          where('date', '<=', hoursRange.endDate)
        );
        const snapshot = await getDocs(q);
        const records = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.date > b.date ? -1 : 1));
        if (!cancelled) setTimeRecords(records);
      } catch (error) {
        console.error('Error loading hours worked:', error);
        if (!cancelled) setTimeRecords([]);
      } finally {
        if (!cancelled) setLoadingHours(false);
      }
    };
    loadHours();
    return () => { cancelled = true; };
  }, [id, hoursRange.startDate, hoursRange.endDate]);

  const hoursSummary = useMemo(() => {
    const totalHours = timeRecords.reduce((sum, r) => sum + (r.hoursWorked || 0), 0);
    const daysWorked = timeRecords.filter(r => (r.hoursWorked || 0) > 0).length;
    const stillOpen = timeRecords.filter(r => r.status === 'clocked_in').length;
    return {
      totalHours,
      daysWorked,
      stillOpen,
      avgHoursPerDay: daysWorked > 0 ? totalHours / daysWorked : 0
    };
  }, [timeRecords]);

  const applyHoursPreset = (preset) => {
    const today = new Date();
    if (preset === 'thisWeek') {
      setHoursRange({
        startDate: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      });
    } else if (preset === 'lastWeek') {
      const lastWeekDay = subDays(today, 7);
      setHoursRange({
        startDate: format(startOfWeek(lastWeekDay, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(endOfWeek(lastWeekDay, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      });
    } else if (preset === 'thisMonth') {
      setHoursRange({
        startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(today), 'yyyy-MM-dd')
      });
    } else if (preset === 'last30') {
      setHoursRange({
        startDate: format(subDays(today, 29), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      });
    } else if (preset === 'last90') {
      setHoursRange({
        startDate: format(subDays(today, 89), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      });
    }
  };

  const formatTimeOfDay = (iso) => {
    if (!iso) return '—';
    try {
      return format(new Date(iso), 'h:mm a');
    } catch {
      return '—';
    }
  };

  // Get time off requests for this employee
  const timeOffRequests = useMemo(() => {
    if (!id) return [];
    return getEmployeeTimeOff(id)
      .filter(r => r.status !== 'cancelled')
      .sort((a, b) => new Date(b.requestedAt || b.createdAt) - new Date(a.requestedAt || a.createdAt));
  }, [id, getEmployeeTimeOff]);

  if (!employee) {
    return (
      <div className="detail-page">
        <div className="not-found">
          <h2>Employee not found</h2>
          <Link to="/employees" className="btn btn-secondary">
            <ArrowLeft size={18} />
            Back to Employees
          </Link>
        </div>
      </div>
    );
  }

  const roleConfig = employeeRoles[employee.role];

  const handleSubmitTimeOff = async (e) => {
    e.preventDefault();
    if (!formData.startDate) {
      alert('Please select a start date');
      return;
    }
    setSubmitting(true);
    try {
      const request = await addTimeOffRequest({
        employeeId: id,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        type: formData.type,
        reason: formData.reason,
        submittedByManager: true
      });
      let wasAutoApproved = false;
      if (formData.autoApprove) {
        if (isOwnTimeOffRequest(request.id)) {
          alert('Time off submitted as pending. You cannot auto-approve your own request — another manager must review it.');
        } else {
          await approveTimeOffRequest(request.id, 'Submitted & approved by manager');
          wasAutoApproved = true;
        }
      }
      setFormData({ startDate: '', endDate: '', type: 'pto', reason: '', autoApprove: true });
      setShowAddForm(false);
      if (!formData.autoApprove || wasAutoApproved) {
        alert(`Time off ${wasAutoApproved ? 'submitted & approved' : 'submitted as pending'}!`);
      }
    } catch (error) {
      console.error('Error submitting time off:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const addFormDayCount = formData.startDate 
    ? differenceInDays(parseISO(formData.endDate || formData.startDate), parseISO(formData.startDate)) + 1 
    : 0;

  return (
    <div className="detail-page">
      <div className="detail-header">
        <Link to="/employees" className="back-link">
          <ArrowLeft size={18} />
          Back to Employees
        </Link>
        
        <div className="detail-header-content">
          <div className="detail-title-section">
            <h1>
              {formatFullName(employee)}
              {employee.role === 'lead' && <Star size={24} className="lead-star-large" />}
            </h1>
            <div className="detail-badges">
              <span className={`status-badge ${employee.status}`}>{employee.status}</span>
              <span className="type-badge">{roleConfig?.label}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3>Contact Information</h3>
          <div className="info-list">
            <div className="info-item">
              <Phone size={18} />
              <div>
                <span className="info-label">Phone</span>
                <span className="info-value">{formatPhone(employee.phone)}</span>
              </div>
            </div>
            <div className="info-item">
              <Mail size={18} />
              <div>
                <span className="info-label">Email</span>
                <span className="info-value">{employee.email}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h3>Employment Details</h3>
          <div className="info-list">
            <div className="info-item">
              <DollarSign size={18} />
              <div>
                <span className="info-label">{employee.payRates?.length > 0 ? 'Default Rate' : 'Hourly Rate'}</span>
                <span className="info-value">{formatCurrency(employee.hourlyRate)} / hour</span>
              </div>
            </div>
            {employee.payRates?.length > 0 && employee.payRates.map((pr, idx) => (
              <div className="info-item" key={idx}>
                <DollarSign size={18} />
                <div>
                  <span className="info-label">{pr.label}</span>
                  <span className="info-value">{formatCurrency(pr.rate)} / hour</span>
                </div>
              </div>
            ))}
            <div className="info-item">
              <Calendar size={18} />
              <div>
                <span className="info-label">Hire Date</span>
                <span className="info-value">{formatDate(employee.hireDate)}</span>
              </div>
            </div>
          </div>
        </div>

        {employee.skills && employee.skills.length > 0 && (
          <div className="detail-card full-width">
            <h3>Skills & Certifications</h3>
            <div className="skills-list">
              {employee.skills.map(skill => (
                <span key={skill} className="skill-tag">
                  {skill.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hours Worked */}
        <div className="detail-card full-width">
          <div className="hours-header">
            <h3><Clock size={18} /> Hours Worked</h3>
            <div className="hours-presets">
              <button type="button" className="hours-preset-btn" onClick={() => applyHoursPreset('thisWeek')}>This Week</button>
              <button type="button" className="hours-preset-btn" onClick={() => applyHoursPreset('lastWeek')}>Last Week</button>
              <button type="button" className="hours-preset-btn" onClick={() => applyHoursPreset('thisMonth')}>This Month</button>
              <button type="button" className="hours-preset-btn" onClick={() => applyHoursPreset('last30')}>Last 30 Days</button>
              <button type="button" className="hours-preset-btn" onClick={() => applyHoursPreset('last90')}>Last 90 Days</button>
            </div>
          </div>

          <div className="hours-range-row">
            <div className="hours-range-group">
              <label>From</label>
              <input
                type="date"
                value={hoursRange.startDate}
                max={hoursRange.endDate}
                onChange={(e) => setHoursRange({ ...hoursRange, startDate: e.target.value })}
              />
            </div>
            <div className="hours-range-group">
              <label>To</label>
              <input
                type="date"
                value={hoursRange.endDate}
                min={hoursRange.startDate}
                onChange={(e) => setHoursRange({ ...hoursRange, endDate: e.target.value })}
              />
            </div>
          </div>

          <div className="hours-summary">
            <div className="hours-summary-card">
              <span className="hours-summary-value">{hoursSummary.totalHours.toFixed(2)}</span>
              <span className="hours-summary-label">Total Hours</span>
            </div>
            <div className="hours-summary-card">
              <span className="hours-summary-value">{hoursSummary.daysWorked}</span>
              <span className="hours-summary-label">Days Worked</span>
            </div>
            <div className="hours-summary-card">
              <span className="hours-summary-value">{hoursSummary.avgHoursPerDay.toFixed(2)}</span>
              <span className="hours-summary-label">Avg Hours/Day</span>
            </div>
            <div className="hours-summary-card">
              <span className="hours-summary-value">
                {formatCurrency((employee.hourlyRate || 0) * hoursSummary.totalHours)}
              </span>
              <span className="hours-summary-label">Est. Pay (default rate)</span>
            </div>
          </div>

          {loadingHours ? (
            <div className="empty-timeoff">Loading hours...</div>
          ) : timeRecords.length === 0 ? (
            <div className="empty-timeoff">No time entries in this date range</div>
          ) : (
            <div className="hours-entry-list">
              {hoursSummary.stillOpen > 0 && (
                <div className="hours-open-banner">
                  <AlertTriangle size={14} />
                  {hoursSummary.stillOpen} open clock-in{hoursSummary.stillOpen !== 1 ? 's' : ''} (no clock out yet)
                </div>
              )}
              <div className="hours-entry-row hours-entry-head">
                <span>Date</span>
                <span>Clock In</span>
                <span>Clock Out</span>
                <span>Hours</span>
              </div>
              {timeRecords.map(rec => (
                <div key={rec.id} className="hours-entry-row">
                  <span className="hours-entry-date">
                    {format(parseISO(rec.date), 'EEE, MMM d, yyyy')}
                    {rec.manualEntry && <span className="hours-entry-tag">Manual</span>}
                  </span>
                  <span>{formatTimeOfDay(rec.clockInTime)}</span>
                  <span>
                    {rec.clockOutTime
                      ? formatTimeOfDay(rec.clockOutTime)
                      : <span className="hours-entry-open">Still clocked in</span>}
                  </span>
                  <span className="hours-entry-hours">
                    {rec.hoursWorked ? `${rec.hoursWorked.toFixed(2)}h` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Time Off History */}
        <div className="detail-card full-width">
          <div className="timeoff-header">
            <h3><Calendar size={18} /> Time Off Requests</h3>
            <button 
              className={`btn ${showAddForm ? 'btn-secondary' : 'btn-primary'} btn-sm`}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add Time Off</>}
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleSubmitTimeOff} className="manager-timeoff-form">
              <div className="mtf-row">
                <div className="mtf-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="mtf-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    min={formData.startDate}
                  />
                  <span className="mtf-hint">Leave blank for single day</span>
                </div>
                <div className="mtf-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick Leave</option>
                    <option value="personal">Personal Day</option>
                    <option value="unpaid">Unpaid Leave</option>
                  </select>
                </div>
              </div>
              <div className="mtf-row">
                <div className="mtf-group mtf-grow">
                  <label>Reason (optional)</label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="e.g. Doctor appointment, family event..."
                  />
                </div>
              </div>
              <div className="mtf-footer">
                <div className="mtf-left">
                  {addFormDayCount > 0 && (
                    <span className="mtf-day-count">
                      <Clock size={13} /> {addFormDayCount} day{addFormDayCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  <label className="mtf-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.autoApprove}
                      onChange={(e) => setFormData({ ...formData, autoApprove: e.target.checked })}
                    />
                    Auto-approve
                  </label>
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                  {submitting ? 'Submitting...' : <><Send size={14} /> Submit</>}
                </button>
              </div>
            </form>
          )}

          {timeOffRequests.length === 0 && !showAddForm ? (
            <div className="empty-timeoff">No time off requests</div>
          ) : (
            <div className="timeoff-history-list">
              {timeOffRequests.map(req => {
                const typeLabels = { pto: 'PTO', sick: 'Sick', personal: 'Personal', unpaid: 'Unpaid' };
                const dayCount = differenceInDays(parseISO(req.endDate), parseISO(req.startDate)) + 1;
                const isExpired = isPast(parseISO(req.endDate));
                
                return (
                  <div key={req.id} className={`timeoff-row ${req.status} ${isExpired ? 'past' : ''}`}>
                    <div className="timeoff-row-left">
                      <span className={`timeoff-status-dot ${req.status}`}>
                        {req.status === 'approved' && <Check size={12} />}
                        {req.status === 'denied' && <XCircle size={12} />}
                        {req.status === 'pending' && <Clock size={12} />}
                      </span>
                      <div className="timeoff-row-info">
                        <span className="timeoff-date-range">
                          {format(parseISO(req.startDate), 'MMM d')}
                          {req.endDate !== req.startDate && <> – {format(parseISO(req.endDate), 'MMM d')}</>}
                          {', '}{format(parseISO(req.startDate), 'yyyy')}
                        </span>
                        <span className="timeoff-meta">
                          {typeLabels[req.type] || req.type} · {dayCount} day{dayCount !== 1 ? 's' : ''}
                          {req.reason && <> · "{req.reason}"</>}
                          {req.submittedByManager && <> · <em>Added by manager</em></>}
                        </span>
                      </div>
                    </div>
                    <span className={`timeoff-status-badge ${req.status}`}>
                      {req.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="detail-card full-width">
          <EmployeeDocuments employee={employee} />
        </div>

        {/* Conversations */}
        <div className="detail-card full-width">
          <EmployeeConversations employee={employee} />
        </div>

        {/* Performance Reviews */}
        <div className="detail-card full-width">
          <EmployeeReviewSection employee={employee} />
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetail;
